import { User } from '@supabase/supabase-js';

/**
 * Extended Supabase User with additional fields from database
 *
 * These fields are added by AuthContext.loadCompleteUserProfile()
 * after fetching from the users table. This provides type-safe access
 * to database fields that are attached to the auth user object.
 */
export type ExtendedUser = User & {
  /** User roles from users.roles column (JSONB array) */
  roles: string[];

  /** User's organization ID from users.organization_id column */
  organization_id: string;

  /** User's first name from users.first_name column */
  firstName: string;

  /** User's last name from users.last_name column */
  lastName: string;

  /** User's full name from users.full_name column */
  fullName: string;

  /** User's employment status from users.employment_status column */
  employment_status?: string;
};

/**
 * Type guard to check if a user object has been extended with database fields
 *
 * @param user - User object from auth context
 * @returns true if user has extended fields, false otherwise
 *
 * @example
 * const { user } = useAuth();
 * if (isExtendedUser(user)) {
 *   console.log(user.roles); // TypeScript knows this exists
 * }
 */
export function isExtendedUser(user: User | null | undefined): user is ExtendedUser {
  return user !== null && user !== undefined && 'roles' in user;
}

/**
 * Helper to safely get user roles with fallback
 *
 * @param user - User object (may or may not be extended)
 * @returns Array of role strings, empty array if not extended or null
 */
export function getUserRoles(user: User | null | undefined): string[] {
  if (isExtendedUser(user)) {
    return user.roles;
  }
  return [];
}
